// src/pages/admin/PageBankAccounts.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

const EMPTY_FORM = {
  companyId: "",
  currency: "AED",
  accountName: "",
  bankName: "",
  branchName: "",
  accountNumber: "",
  ibanNumber: "",
  swiftCode: "",
};

function BankForm({ form, setForm, companies, onSave, onCancel, saving, error }) {
  function setF(key, val) { setForm(f => ({ ...f, [key]: val })); }

  const fields = [
    ["accountName",   "Account Name *",   "text",   "Al Mehraj Adhesives Trading LLC"],
    ["bankName",      "Bank Name *",       "text",   "Emirates NBD"],
    ["branchName",    "Branch Name *",     "text",   "Deira Branch"],
    ["accountNumber", "Account Number *",  "text",   "1234567890"],
    ["ibanNumber",    "IBAN Number *",     "text",   "AE07 0331 2345 6789 0123 456"],
    ["swiftCode",     "SWIFT Code *",      "text",   "EBILAEAD"],
  ];

  return (
    <div>
      <div className="form-row">
        <span className="form-label">Company *</span>
        <select className="form-select" style={{ width: 220 }}
          value={form.companyId} onChange={e => setF("companyId", e.target.value)}>
          <option value="">— Select —</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.businessUnit} — {c.name}</option>
          ))}
        </select>
      </div>

      <div className="form-row">
        <span className="form-label">Currency *</span>
        <select className="form-select" style={{ width: 120 }}
          value={form.currency} onChange={e => setF("currency", e.target.value)}>
          <option value="AED">AED</option>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </select>
      </div>

      {fields.map(([key, label, type, placeholder]) => (
        <div className="form-row" key={key}>
          <span className="form-label">{label}</span>
          <input className="form-input" style={{ flex: 1 }} type={type}
            placeholder={placeholder}
            value={form[key]}
            onChange={e => setF(key, e.target.value)} />
        </div>
      ))}

      {error && <div className="alert-warn" style={{ marginTop: 8 }}>{error}</div>}

      <div className="form-sep" />
      <div className="form-footer">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" disabled={saving} onClick={onSave}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export function PageBankAccounts({ companies }) {
  const showToast = useToast();
  const [banks,   setBanks]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const [showAdd,  setShowAdd]  = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [form,      setForm]      = useState({ ...EMPTY_FORM });
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try { setBanks(await api.fetchBankAccounts()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openAdd() {
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setEditing(null);
    setShowAdd(true);
  }

  function openEdit(b) {
    setForm({
      companyId:     String(b.companyId),
      currency:      b.currency,
      accountName:   b.accountName,
      bankName:      b.bankName,
      branchName:    b.branchName,
      accountNumber: b.accountNumber,
      ibanNumber:    b.ibanNumber,
      swiftCode:     b.swiftCode,
    });
    setFormError(null);
    setEditing(b);
    setShowAdd(true);
  }

  function cancelForm() {
    setShowAdd(false);
    setEditing(null);
    setFormError(null);
  }

  async function handleSave() {
    if (!form.companyId || !form.currency || !form.accountName.trim() ||
        !form.bankName.trim() || !form.branchName.trim() ||
        !form.accountNumber.trim() || !form.ibanNumber.trim() || !form.swiftCode.trim()) {
      setFormError("All fields are required.");
      return;
    }
    setSaving(true); setFormError(null);
    try {
      const payload = {
        companyId:     Number(form.companyId),
        currency:      form.currency,
        accountName:   form.accountName.trim(),
        bankName:      form.bankName.trim(),
        branchName:    form.branchName.trim(),
        accountNumber: form.accountNumber.trim(),
        ibanNumber:    form.ibanNumber.trim(),
        swiftCode:     form.swiftCode.trim().toUpperCase(),
      };
      if (editing) {
        await api.updateBankAccount(editing.id, payload);
      } else {
        await api.createBankAccount(payload);
      }
      await load();
      showToast("Bank account saved");
      cancelForm();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await api.deleteBankAccount(id);
      setDeleting(null);
      await load();
      showToast("Deleted", "info");
    } catch (e) {
      alert("Delete failed: " + e.message);
    }
  }

  const companyLabel = (companyId) => {
    const c = (companies || []).find(c => c.id === companyId);
    return c ? `${c.businessUnit} — ${c.name}` : `Company #${companyId}`;
  };

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Bank Accounts</div>
          <div className="page-subtitle">Company bank details for documents and payments</div>
        </div>
        <div className="page-header-right">
          {!showAdd && (
            <button className="btn-primary" onClick={openAdd}>+ Add Bank Account</button>
          )}
        </div>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {/* Add / Edit form */}
      {showAdd && (
        <div className="card" style={{ marginBottom: 20, maxWidth: 620 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
            {editing ? "Edit Bank Account" : "Add Bank Account"}
          </div>
          <BankForm
            form={form}
            setForm={setForm}
            companies={companies || []}
            onSave={handleSave}
            onCancel={cancelForm}
            saving={saving}
            error={formError}
          />
        </div>
      )}

      {/* Bank accounts list */}
      {!loading && banks.length === 0 && !showAdd && (
        <div style={{ color: "#888", fontSize: 13, padding: "24px 0" }}>
          No bank accounts added yet.
        </div>
      )}

      {banks.map(b => (
        <div key={b.id} className="card" style={{ marginBottom: 14, maxWidth: 680 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              {/* Header row */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{b.accountName}</span>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                  background: b.currency === "USD" ? "#dbeafe" : "#d1fae5",
                  color:      b.currency === "USD" ? "#1d4ed8" : "#065f46",
                }}>{b.currency}</span>
                <span style={{
                  fontSize: 11, padding: "2px 8px", borderRadius: 4,
                  background: "#f3f4f6", color: "#6b7280",
                }}>{companyLabel(b.companyId)}</span>
              </div>

              {/* Details grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px", fontSize: 12 }}>
                {[
                  ["Bank",           b.bankName],
                  ["Branch",         b.branchName],
                  ["Account No.",    b.accountNumber],
                  ["IBAN",           b.ibanNumber],
                  ["SWIFT / BIC",    b.swiftCode],
                ].map(([label, value]) => (
                  <div key={label}>
                    <span style={{ color: "#888", marginRight: 6 }}>{label}:</span>
                    <span style={{ fontWeight: 500, fontFamily: label === "IBAN" || label === "SWIFT / BIC" ? "monospace" : "inherit" }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 6, marginLeft: 16, flexShrink: 0 }}>
              <button className="btn-small" onClick={() => openEdit(b)}>Edit</button>
              <button className="btn-small" style={{ color: "#dc2626" }}
                onClick={() => setDeleting(b)}>Delete</button>
            </div>
          </div>
        </div>
      ))}

      {/* Delete confirmation */}
      {deleting && (
        <div className="overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div className="modal-title">Delete Bank Account</div>
              <button className="modal-close" onClick={() => setDeleting(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16 }}>
                Delete <strong>{deleting.accountName}</strong> ({deleting.currency} — {deleting.bankName})?
                This cannot be undone.
              </p>
              <div className="form-footer">
                <button className="btn" onClick={() => setDeleting(null)}>Cancel</button>
                <button className="btn" style={{ background: "#dc2626", color: "#fff", border: "none" }}
                  onClick={() => handleDelete(deleting.id)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
